/**
 * DateRangeFilter Component for Sales Dashboard
 *
 * Provides flexible date range filtering options:
 * - Last 7 Days (default)
 * - Last 30 Days
 * - Last 90 Days
 * - This Month
 * - Custom Date Range
 *
 * Emits selected date range via callback function
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, ChevronDown } from 'lucide-react';
import { DateRange } from '../types';

interface DateRangeFilterProps {
  onDateRangeChange: (dateRange: DateRange) => void;
  selectedLabel?: string;
}

/**
 * Get today's date in YYYY-MM-DD format
 */
function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get date from N days ago
 */
function getDaysAgoDate(days: number): string {
  const date = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return date.toISOString().split('T')[0];
}

/**
 * Get the start of current month
 */
function getMonthStartDate(): string {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return monthStart.toISOString().split('T')[0];
}

/**
 * DateRangeFilter Component
 */
export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  onDateRangeChange,
  selectedLabel = 'Last 7 Days',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLabel, setCurrentLabel] = useState<string>(selectedLabel);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const today = getTodayDate();

  // Predefined ranges (customized for Sales Dashboard)
  const predefinedRanges: DateRange[] = [
    {
      label: 'All Time',
      startDate: '2000-01-01',
      endDate: today,
    },
    {
      label: 'Last 7 Days',
      startDate: getDaysAgoDate(7),
      endDate: today,
    },
    {
      label: 'Last 30 Days',
      startDate: getDaysAgoDate(30),
      endDate: today,
    },
    {
      label: 'Last 90 Days',
      startDate: getDaysAgoDate(90),
      endDate: today,
    },
    {
      label: 'This Month',
      startDate: getMonthStartDate(),
      endDate: today,
    },
  ];

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  /**
   * Handle predefined range selection
   */
  const handleSelectRange = (range: DateRange) => {
    setCurrentLabel(range.label);
    setShowCustomInput(false);
    setCustomStartDate('');
    setCustomEndDate('');
    onDateRangeChange(range);
    setIsOpen(false);
  };

  /**
   * Handle custom date range
   */
  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      // Validate date range
      if (new Date(customStartDate) > new Date(customEndDate)) {
        alert('Start date must be before end date');
        return;
      }

      const customRange: DateRange = {
        label: `${formatDisplayDate(customStartDate)} - ${formatDisplayDate(customEndDate)}`,
        startDate: customStartDate,
        endDate: customEndDate,
      };

      setCurrentLabel(customRange.label);
      onDateRangeChange(customRange);
      setIsOpen(false);
    }
  };

  /**
   * Clear custom date inputs
   */
  const handleClearCustom = () => {
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomInput(false);
  };

  /**
   * Format date for display (e.g., "Dec 10, 2025")
   */
  function formatDisplayDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Toggle dropdown
   */
  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Filter Button */}
      <Button
        variant="outline"
        onClick={toggleDropdown}
        className="flex items-center gap-2 bg-white border-gray-200 hover:bg-gray-50"
      >
        <Calendar className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">{currentLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
          {/* Quick Select Section */}
          <div className="px-2 py-2 border-b border-gray-200">
            <div className="px-2 py-1.5 text-xs font-semibold text-gray-700">Quick Select</div>
            <div className="space-y-1 mt-1">
              {predefinedRanges.map((range) => (
                <button
                  key={range.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectRange(range);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    currentLabel === range.label
                      ? 'bg-blue-100 text-blue-900 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Range Section */}
          <div className="px-2 py-2">
            <div className="px-2 py-1.5 text-xs font-semibold text-gray-700">Custom Range</div>

            {!showCustomInput ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCustomInput(true);
                }}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Select Custom Date Range
              </button>
            ) : (
              <div className="space-y-3 mt-3 p-3 bg-gray-50 rounded-md">
                {/* Start Date Input */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* End Date Input */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApplyCustomRange();
                    }}
                    className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearCustom();
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
